import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useCheckoutContext } from '@/common/app/contexts/CheckoutContext';
import { UserInfo } from '@/entities/Checkout/domain/Checkout.domain';
import { saveUser, TSaveUserRequest } from '@/entities/Checkout/service/postApiSaveUser';
import { useContactInfo } from '@/entities/Forms/contactInfo';
import { CustomInput } from '@/entities/Forms/inputs/CustomInput';
import { CustomPhoneInput } from '@/entities/Forms/inputs/CustomPhoneInput';
import { CustomCheckbox } from '@/entities/Forms/inputs/CustomCheckbox';
import { useInView } from 'react-intersection-observer';
import { useCartContext } from '@/common/app/contexts';
import { EVENT, trackCheckout } from '@/common/app/utils/iterable';
import * as S from './ContactInfo.styles';

type ContactInfoProps = {
    isContactInfoConfirmed: boolean;
    setIsContactInfoConfirmed: (value: boolean) => void;
};

const ContactInfo = ({ setIsContactInfoConfirmed }: ContactInfoProps): ReactElement => {
    const { saveUserInfo } = useCheckoutContext();
    const { submitContinue, cartData, activities } = useCartContext();
    const [errorMessages, setErrorMessages] = useState<string[]>();
    const [showSecondPhone, setShowSecondPhone] = useState(
        () =>
            !!useContactInfo.getState().values.phone2 &&
            +useContactInfo.getState().values.phone2 > 9
    );
    const [checkoutStarted, setCheckoutStarted] = useState(false);
    const [crsTracked, setCrsTracked] = useState(false);

    const isValid = useContactInfo((s) => s.isValid);

    // Sync isContactInfoConfirmed with form validity on mount (when hydrated from localStorage)
    useEffect(() => {
        if (isValid) {
            handleSubmit();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = useCallback(async () => {
        const { values } = useContactInfo.getState();

        const formData: TSaveUserRequest = {
            user_id: '',
            user_info: {
                first_name: values.first_name,
                last_name: values.last_name,
                phone: values.phone,
                email: values.email,
                phone2: +values.phone2 > 9 ? values.phone2 : '',
            },
            send_sms: values.send_sms,
        };

        const userInfo = await saveUser(formData);

        if (userInfo?.errors) {
            const errors = Array.isArray(userInfo.errors) ? userInfo.errors : ['Wrong payload'];
            setErrorMessages(errors);

            setTimeout(() => {
                setErrorMessages(undefined);
            }, 5000);
            return;
        }

        const nextUserId = userInfo?.data?.user_id;

        const newUserInfo: UserInfo = {
            ...values,
            userId: nextUserId ?? null,
        };

        saveUserInfo?.(newUserInfo, checkoutStarted);
        setIsContactInfoConfirmed(true);

        // Save userId to form store so it gets persisted to localStorage
        useContactInfo.getState().updateField('userId', nextUserId ?? null, false);
    }, [checkoutStarted, saveUserInfo, setIsContactInfoConfirmed]);

    // Submit on blur when form is valid
    const handleBlur = useCallback(() => {
        if (isValid) {
            handleSubmit();
        } else {
            setIsContactInfoConfirmed(false);
        }
    }, [isValid, handleSubmit, setIsContactInfoConfirmed]);

    useEffect(() => {
        if (!crsTracked && isValid) {
            const { email } = useContactInfo.getState().values;
            if (email) {
                submitContinue(email);
                setCrsTracked(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid, crsTracked]);

    const [ref] = useInView({
        threshold: 1,
        delay: 100,
        trackVisibility: true,
        triggerOnce: true,
        onChange: (inView) => {
            if (inView) {
                setCheckoutStarted((prev) => {
                    if (!prev && isValid) {
                        trackCheckout(
                            EVENT.checkoutPayment,
                            cartData.cart_items,
                            undefined,
                            activities
                        );
                    }
                    return true;
                });
            }
        },
    });

    return (
        <S.ContactInfoContainer id="contact-info-form" ref={ref}>
            <S.FormWrapper>
                <S.FormContainer>
                    <CustomInput
                        formName="contactInfo"
                        field="first_name"
                        name="firstName"
                        label="First Name"
                        required
                        placeholder="Your first name"
                        autoComplete="given-name"
                        debounce
                        onBlur={handleBlur}
                    />
                </S.FormContainer>
                <S.FormContainer>
                    <CustomInput
                        formName="contactInfo"
                        field="last_name"
                        name="lastName"
                        label="Last Name"
                        required
                        placeholder="Your last name"
                        autoComplete="family-name"
                        debounce
                        onBlur={handleBlur}
                    />
                </S.FormContainer>
                <S.FormContainer>
                    <CustomInput
                        formName="contactInfo"
                        field="email"
                        name="email"
                        label="Email"
                        required
                        placeholder="Your email"
                        autoComplete="email"
                        type="email"
                        debounce
                        onBlur={handleBlur}
                    />
                </S.FormContainer>
                <S.FormContainer>
                    <CustomPhoneInput
                        formName="contactInfo"
                        field="phone"
                        label="Mobile Phone"
                        required
                        inputProps={{ name: 'phone', id: 'phone', autoComplete: 'tel' }}
                        postBtn={!showSecondPhone}
                        onBtnClick={() => setShowSecondPhone(true)}
                        onBlur={handleBlur}
                    />
                </S.FormContainer>
                {showSecondPhone && (
                    <S.FormContainer>
                        <CustomPhoneInput
                            formName="contactInfo"
                            field="phone2"
                            label="Secondary Phone"
                            inputProps={{
                                name: 'secondary_phone',
                                id: 'secondary_phone',
                                autoComplete: 'tel',
                            }}
                            onBlur={handleBlur}
                        />
                    </S.FormContainer>
                )}
                <S.BottomInfo disabled={false}>
                    <CustomCheckbox
                        formName="contactInfo"
                        field="send_sms"
                        name="sms-agreement"
                        label="Text me my booking confirmation"
                        onChange={handleBlur}
                    />
                    {errorMessages &&
                        errorMessages.map((error, index) => (
                            <S.ServerError key={index}>{error}</S.ServerError>
                        ))}
                </S.BottomInfo>
            </S.FormWrapper>
        </S.ContactInfoContainer>
    );
};

export { ContactInfo };
